// Public Next-Steps inquiry page. QR landing at /inquiry/:code.
// Collects the same contact-card fields as the Coordinator directory and drops
// into the org's Coordinator "Inquiries" inbox on the Next Steps page.

import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, ArrowLeft, UserPlus } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: `${BACKEND_URL}/api` });

const AGE_GROUPS = ["Child", "Youth", "Young Adult", "Adult", "Senior"];
const REFERRAL_SOURCES = ["Invited by friend", "Family", "Social media", "Web search", "Walked in", "Other"];

function Card({ children }) {
  return (
    <div
      className="w-full max-w-md rounded-lg px-6 py-8 mx-auto"
      style={{ background: "var(--bg-alt)", border: "1px solid var(--border-subtle)" }}
      data-testid="inquiry-card"
    >
      {children}
    </div>
  );
}

export default function Inquiry() {
  const { code } = useParams();
  const [orgName, setOrgName] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", gender: "",
    email: "", phone: "", address_city: "",
    age_group: "", referral_source: "", invited_by: "", notes: "",
  });

  useEffect(() => {
    api.get(`/public/inquiry/${code}`)
      .then((r) => setOrgName(r.data.org_name))
      .catch((e) => setError(e?.response?.data?.detail || "This inquiry link isn't valid."))
      .finally(() => setLoading(false));
  }, [code]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.first_name.trim()) {
      setError("Please enter at least your first name.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post(`/public/inquiry/${code}`, {
        ...form,
        gender: form.gender || null,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-base)" }}><p style={{ color: "var(--muted)" }}>Loading…</p></div>;
  }

  if (error && !orgName) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--bg-base)" }}>
        <Card>
          <h1 className="font-serif-display text-2xl mb-2" style={{ color: "var(--ink)" }}>We couldn't find this inquiry link.</h1>
          <p className="text-sm" style={{ color: "var(--ink-2)" }}>Please double-check the QR code or ask the church for a fresh link.</p>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4" style={{ background: "var(--bg-base)" }}>
        <Card>
          <div className="flex flex-col items-center text-center">
            <div className="rounded-full p-4 mb-4" style={{ background: "rgba(168,164,110,0.15)" }}>
              <CheckCircle2 size={48} style={{ color: "var(--olive)" }} />
            </div>
            <h2 className="font-serif-display text-3xl mb-2" style={{ color: "var(--ink)" }}>Thank you!</h2>
            <p className="text-sm" style={{ color: "var(--ink-2)" }}>
              <strong>{orgName}</strong> will reach out shortly about joining a Next Steps class.
            </p>
            <p className="text-xs mt-6 max-w-xs" style={{ color: "var(--muted)" }}>
              Grateful you're taking the next step. We're excited to walk with you.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-start justify-center px-4 py-10" style={{ background: "var(--bg-base)" }}>
      <Card>
        <div className="flex items-center gap-2 mb-1" style={{ color: "var(--muted)" }}>
          <UserPlus size={14} />
          <span className="text-[0.6rem] uppercase tracking-[0.22em]">Inquiry · {orgName}</span>
        </div>
        <h1 className="font-serif-display text-3xl leading-tight mb-2" style={{ color: "var(--ink)" }}>Tell us a little about yourself.</h1>
        <p className="text-xs mb-5" style={{ color: "var(--ink-2)" }}>
          A Coordinator will follow up to place you in the right Next Steps class. Only first name is required — share whatever feels right.
        </p>

        <form onSubmit={submit} className="space-y-3" data-testid="inquiry-form">
          <div className="grid grid-cols-2 gap-2">
            <Input label="First name *" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="inquiry-first-name" required />
            <Input label="Last name"    value={form.last_name}  onChange={(v) => setForm({ ...form, last_name: v })}  testid="inquiry-last-name" />
          </div>

          <div>
            <Label>Title</Label>
            <div className="flex gap-2">
              {[
                { v: "", label: "Prefer not to say" },
                { v: "male", label: "Bro." },
                { v: "female", label: "Sis." },
              ].map((o) => (
                <button
                  key={o.v || "none"}
                  type="button"
                  onClick={() => setForm({ ...form, gender: o.v })}
                  className="flex-1 px-3 py-2 text-xs border transition-colors"
                  style={{
                    borderColor: form.gender === o.v ? "var(--gold)" : "var(--border-subtle)",
                    background: form.gender === o.v ? "var(--bg-base)" : "transparent",
                    color: "var(--ink)",
                  }}
                  data-testid={`inquiry-gender-${o.v || "none"}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          <Input label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} type="email" testid="inquiry-email" />
          <Input label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} type="tel" testid="inquiry-phone" />
          <Input label="City"  value={form.address_city} onChange={(v) => setForm({ ...form, address_city: v })} testid="inquiry-city" />

          <Select label="Age group" value={form.age_group} onChange={(v) => setForm({ ...form, age_group: v })} options={AGE_GROUPS} testid="inquiry-age" />
          <Select label="How did you hear about us?" value={form.referral_source} onChange={(v) => setForm({ ...form, referral_source: v })} options={REFERRAL_SOURCES} testid="inquiry-referral" />
          <Input label="Who invited you?" value={form.invited_by} onChange={(v) => setForm({ ...form, invited_by: v })} testid="inquiry-invited" />

          <div>
            <Label>Anything else?</Label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3}
              className="w-full px-3 py-2 text-sm border"
              style={{ borderColor: "var(--border-strong)", background: "var(--bg-base)", color: "var(--ink)" }}
              data-testid="inquiry-notes"
            />
          </div>

          {error && <p className="text-xs" style={{ color: "var(--terracotta)" }} data-testid="inquiry-error">{error}</p>}

          <button
            type="submit"
            disabled={busy}
            className="btn-primary w-full justify-center"
            data-testid="inquiry-submit"
          >
            {busy ? "Submitting…" : "Send inquiry"}
          </button>
          <p className="text-[0.65rem] text-center mt-2" style={{ color: "var(--muted)" }}>
            Your info is only shared with {orgName}'s Coordinator team.
          </p>
        </form>
      </Card>
    </div>
  );
}

function Label({ children }) {
  return (
    <label className="block text-[0.6rem] uppercase tracking-[0.18em] mb-1" style={{ color: "var(--muted)" }}>{children}</label>
  );
}

function Input({ label, value, onChange, testid, type = "text", required = false }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={required}
        className="w-full px-3 py-2 text-sm border"
        style={{ borderColor: "var(--border-strong)", background: "var(--bg-base)", color: "var(--ink)" }}
        data-testid={testid}
      />
    </div>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <div>
      <Label>{label}</Label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full px-3 py-2 text-sm border"
        style={{ borderColor: "var(--border-strong)", background: "var(--bg-base)", color: "var(--ink)" }}
        data-testid={testid}
      >
        <option value="">—</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </div>
  );
}
